import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CloseIcon from '@material-ui/icons/Close';
import Actions from './Actions';
import { IconWrapper } from '../../modules/WikiModule/WikiGlossary/WikiGlossary';
import DeleteConfirmationPopup from './DeleteConfirmationPopup';
import ConfirmationDialog from './ConfirmationDialog';
import { dispatch } from '../../redux/store';
import { setSelectedGlossary } from '../../redux/slices/wikiSlice';
import { getAnchorIfExists } from '../../utils';
import TableLightHeaderCheckbox from './TableLightHeaderCheckbox';
import TableLightCellCheckbox from './TableLightCellCheckbox';
import { setTableSelectedRows } from '../../redux/slices/generalSlice';
import CellFilter from '../../modules/clientModule/components/Common/Table/Header/CellFilter';

type Props = {
  headers: any[];
  rows: any[];
  rowsTemplate: string;
  onPreview: Function;
  onEdit?: Function;
  onDelete?: Function;
  onImageDeleted?: Function;
  deleteConfirmation?: string;
  onOK?: Function;
  onCancel?: Function;
  onDetails?: Function;
  onCellClick?: Function;
};
const TableLightBackground = ({
  headers,
  rows,
  rowsTemplate,
  onPreview,
  onEdit = undefined,
  onDelete = undefined,
  onDetails = undefined,
  onImageDeleted,
  deleteConfirmation = '',
  onOK = () => {},
  onCancel = () => {},
  setDeleteRecordCandidate = undefined,
  onCellClick = undefined,
}) => {
  const [showConfirmId, setShowConfirmId] = useState<number>(-1);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  useEffect(() => {
    if (rows && rows.length > 0) {
      const selectedRows = rows.map(row => ({ id: row.id, selected: false }));
      dispatch(setTableSelectedRows(selectedRows));
    }
  }, [rows]);
  const getRowsTemplate = () => {
    if (onEdit || onDelete || onDetails) {
      // Three icon buttons (edit/delete/details) need a little more room than two.
      const actionsWidth = onDetails ? '132px' : '100px';
      return `${rowsTemplate} ${actionsWidth}`;
    }

    return rowsTemplate;
  };

  const onDeleteClicked = row => {
    setConfirmationOpen(true);
    if (setDeleteRecordCandidate) {
      // This will update the redux store with the records that is candidate to be removed.
      // on the popup if the user selects OK, it will be removed
      setDeleteRecordCandidate(row);
    } else {
      dispatch(setSelectedGlossary(row));
    }
  };
  // setTableSelectAll
  return (
    <>
      <ConfirmationDialog
        onOK={onOK}
        onCancel={onCancel}
        confirmation={deleteConfirmation}
        confirmationOpen={confirmationOpen}
        setConfirmationOpen={setConfirmationOpen}
      />
      <TableCard>
      <TableWrapper rowsTemplate={getRowsTemplate()} isHeader>
        {headers.map((item, index) => {
          if (item.selectAll) {
            return <TableLightHeaderCheckbox item={item} index={index} />;
          }
          if (item.showFilter) {
            return (
              <FilteredHeader>
                <HeaderCell key={index}>{item.title}</HeaderCell>
                <CellFilter name={item.key} />
              </FilteredHeader>
            );
          }
          return <HeaderCell key={index}>{item.title}</HeaderCell>;
        })}
        {(onEdit || onDelete || onDetails) && <HeaderCell>Actions</HeaderCell>}
      </TableWrapper>
      {rows.map((rowItem, ndx) => {
        return (
          <TableWrapper rowsTemplate={getRowsTemplate()} zebra={ndx % 2 === 1}>
            {headers.map((item, index) => {
              if (item.checkbox) {
                return <TableLightCellCheckbox item={rowItem} index={index} />;
              }
              if (item.allowLinks) {
                return (
                  <Cell key={index}>
                    <div dangerouslySetInnerHTML={{ __html: getAnchorIfExists(rowItem[item.key]) }} />
                  </Cell>
                );
              }
              if (item.imagesArray) {
                return (
                  <CellColumn key={index}>
                    {rowItem[item.key].map((entry, ndx) => {
                      return (
                        <ImageWrapper key={ndx}>
                          <IconWrapper2 onClick={() => setShowConfirmId(entry.id)}>
                            <CloseIcon />
                            {entry.id === showConfirmId && (
                              <DeleteConfirmationPopup
                                setShowConfirmId={setShowConfirmId}
                                onImageDeleted={onImageDeleted}
                                rowId={rowItem.id}
                                imageId={entry.id}
                              />
                            )}
                          </IconWrapper2>
                          <img key={ndx} onClick={e => onPreview(entry.image)} src={`${entry.image}`} />
                        </ImageWrapper>
                      );
                    })}
                  </CellColumn>
                );
              }
              // Header flag `clickable` turns the cell into a link that hands
              // the row back to the page (used for "show the full list in a
              // popup" cells that would otherwise blow up the row height).
              if (item.clickable && onCellClick && rowItem[item.key]) {
                return (
                  <ClickableCell key={index + ndx} onClick={() => onCellClick(rowItem, item.key)}>
                    {rowItem[item.key]}
                  </ClickableCell>
                );
              }
              if (Array.isArray(rowItem[item.key])) {
                return (
                  <Cell key={index + ndx}>
                    <ArrayDisplay>
                      {rowItem[item.key].map((arrItem, itemIndex) => {
                        return <div key={itemIndex}>{arrItem}</div>;
                      })}
                    </ArrayDisplay>
                  </Cell>
                );
              }
              return <Cell key={index + ndx}>{rowItem[item.key]}</Cell>;
            })}
            {(onEdit || onDelete || onDetails) && (
              <Actions
                row={rowItem}
                onEdit={onEdit}
                onDelete={() => onDeleteClicked(rowItem)}
                onDetails={onDetails}
                showDelete={deleteConfirmation !== ''}
              />
            )}
          </TableWrapper>
        );
      })}
      </TableCard>
    </>
  );
};

export default TableLightBackground;

const ImageWrapper = styled.div`
  position: relative;
`;

const ActionsWrapper = styled.div`
  position: relative;
`;

const IconWrapper2 = styled(IconWrapper)`
  scale: 0.6;
  top: -3px;
  right: 3px;
`;

const ArrayDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilteredHeader = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const TableCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: var(--shadow-xs);
`;

const TableWrapper = styled.div<{ rowsTemplate: string; isHeader?: boolean; zebra?: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ rowsTemplate }) => `${rowsTemplate}`};
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  color: var(--mainText);
  background-color: ${({ isHeader, zebra }) =>
    isHeader
      ? 'var(--table-row-header)'
      : zebra
        ? 'var(--table-row-alt)'
        : 'var(--table-row-base)'};
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease;

  ${({ isHeader }) =>
    !isHeader &&
    `
      &:hover {
        background-color: var(--table-row-hover);
      }
      &:last-child {
        border-bottom: 0;
      }
    `}
`;

export const CellColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 8px;
  padding: 14px 16px;
  background-color: transparent;
  img {
    width: 75px;
  }
`;

export const HeaderCell = styled.div`
  text-align: left;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  input {
    margin-left: 9px;
    margin-right: 10px;
  }
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Cell = styled.div`
  padding: 14px 16px;
  color: var(--mainText);
  font-size: 0.875rem;
  background-color: transparent;
  display: flex;
  align-items: center;
  min-width: 0;
  word-break: break-word;
`;

const ClickableCell = styled(Cell)`
  cursor: pointer;
  color: var(--primary-700);
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--primary-600);
  }
`;

const LinkCell = styled.div`
  background-color: #fff;
  cursor: pointer;
  color: var(--primary);
  text-decoration: underline;
`;
